<template>
  <div>
    <v-navigation-drawer
      v-model="showFactSheet"
      id="fact-sheet-drawer"
      floating
      right
      temporary
      stateless
      hide-overlay
      app
      v-if="profile != null && profileIndicatorsMeta.length > 0"
      :width="factSheetWidth"
      :style="{ marginTop: marginTop + 'px', overflow: 'hidden' }"
    >
      <v-container
        pa-0
        pt-2
        fluid
      >
        <v-layout
          row
          class="no-grow"
          justify-start
          align-start
        >
          <!-- ▼▼▼ ---- BASIC fact sheet ---- ▼▼▼ -->
          <v-flex>
            <v-card
              id="basic-fact-sheet-container"
              flat
              width="300"
              :height="height"
              :max-height="height"
            >
              <v-list
                dense
                class="fact-sheet-list"
                flat
              >
                <!-- basic HEADER - title, subtitle -->
                <v-list-tile class="fact-sheet-header">
                  <v-list-tile-content>
                    <v-list-tile-title>
                      <!-- tooltips used throughout because of long names (countries, topics,...) getting cut off -->
                      <v-tooltip bottom>
                        <span slot="activator">{{ profileHeader.title }}</span>
                        {{ profileHeader.title }}
                      </v-tooltip>
                    </v-list-tile-title>
                    <v-list-tile-sub-title v-if="profileHeader.subtitle !== null">
                      <v-tooltip bottom>
                        <span slot="activator">{{ profileHeader.subtitle }}</span>
                        {{ profileHeader.subtitle }}
                      </v-tooltip>
                    </v-list-tile-sub-title>
                  </v-list-tile-content>

                  <!-- basic CLOSE BTN -->
                  <v-list-tile-action
                    v-if="showExtended === false"
                    @click="showFactSheet = false; showGeneralInfo = true;"
                  >
                    <v-btn icon>
                      <v-icon>close</v-icon>
                    </v-btn>
                  </v-list-tile-action>
                </v-list-tile>
                <v-subheader>
                  {{ profileHeader.yearInfo }}
                  {{ profileHeader.complementInfo }}
                </v-subheader>
                <v-divider></v-divider>

                <!-- basic CONTENT -->
                <!-- General information -->
                <v-list-group
                  v-model="showGeneralInfo"
                >
                  <v-list-tile
                    slot="activator"
                    ripple
                  >
                    <v-list-tile-content>
                      <v-list-tile-title>
                        {{ generalInfo.title }}
                      </v-list-tile-title>
                    </v-list-tile-content>
                  </v-list-tile>
                  <fact-sheet-generalinfo :data="generalInfo.data">
                  </fact-sheet-generalinfo>
                </v-list-group>

                <!-- Public Orgs -->
                <v-list-group
                  ref="publicOrgsList"
                  lazy
                  v-model="showPublicOrgs"
                  class="clickable-group"
                >
                  <v-list-tile
                    slot="activator"
                  >
                    <v-list-tile-content>
                      <v-list-tile-title>
                        {{ topPublicOrgs.title }}
                      </v-list-tile-title>
                    </v-list-tile-content>
                  </v-list-tile>
                  <fact-sheet-actors
                    :active-actors="selectedPublicOrgs"
                    :max-height="publicOrgsListHeight"
                    :progress-active="publicOrgsCount > 0"
                    :progress-indeterminate="publicOrgsProgressBar"
                    :prog-bar-height="progBarHeight"
                    @click:actor="switchPublicOrgsIndicator($event)"
                  >
                    <v-list-tile
                      slot-scope="{actor}"
                      :key="actor.actorId"
                      @click="showExtended = true; displayItemDetails(actor.actorId, $event)"
                      :class="{ secondary: actor.actorId === extendedId }"
                    >
                      <v-list-tile-action class="actor-rank">
                        {{ actor.rank }}
                      </v-list-tile-action>
                      <v-list-tile-content>
                        <v-list-tile-title v-if="actor.labelAlt !== null">{{ actor.labelAlt }}</v-list-tile-title>
                        <v-list-tile-title v-else>{{ actor.label }}</v-list-tile-title>
                      </v-list-tile-content>
                      <v-list-tile-action>
                        {{ actor.value }}
                      </v-list-tile-action>
                    </v-list-tile>
                  </fact-sheet-actors>
                </v-list-group>

                <!-- Firms -->
                <v-list-group
                  ref="firmsList"
                  lazy
                  v-model="showFirms"
                  class="clickable-group"
                >
                  <v-list-tile
                    slot="activator"
                  >
                    <v-list-tile-content>
                      <v-list-tile-title>
                        {{ topFirms.title }}
                      </v-list-tile-title>
                    </v-list-tile-content>
                  </v-list-tile>
                  <fact-sheet-actors
                    :active-actors="selectedFirms"
                    :max-height="firmsListHeight"
                    :progress-active="firmsCount > 0"
                    :progress-indeterminate="firmsProgressBar"
                    :prog-bar-height="progBarHeight"
                    @click:actor="switchFirmsIndicator($event)"
                  >
                    <v-list-tile
                      slot-scope="{actor}"
                      :key="actor.actorId"
                      @click="showExtended = true; displayItemDetails(actor.actorId, $event)"
                      :class="{ secondary: actor.actorId === extendedId }"
                    >
                      <v-list-tile-action class="actor-rank">
                        {{ actor.rank }}
                      </v-list-tile-action>
                      <v-list-tile-content>
                        <v-list-tile-title v-if="actor.labelAlt !== null">{{ actor.labelAlt }}</v-list-tile-title>
                        <v-list-tile-title v-else>{{ actor.label }}</v-list-tile-title>
                      </v-list-tile-content>
                      <v-list-tile-action>
                        {{ actor.value }}
                      </v-list-tile-action>
                    </v-list-tile>
                  </fact-sheet-actors>
                </v-list-group>

                <!-- Social Innovation Projects -->
                <v-list-group
                  ref="sipList"
                  lazy
                  v-model="showSip"
                  :disabled="sipCount === 0 && showSip === false"
                  class="clickable-group"
                >
                  <v-list-tile
                    slot="activator"
                    :inactive="sipCount === 0"
                    :disabled="sipCount === 0"
                  >
                    <v-list-tile-content>
                      <v-list-tile-title>
                        {{ siProjects.title }}
                      </v-list-tile-title>
                    </v-list-tile-content>
                  </v-list-tile>
                    <fact-sheet-sip
                      :data="siProjects.data"
                      :max-height="sipListHeight"
                      :progress-active="sipCount !== 0"
                      :progress-indeterminate="sipProgressBar"
                      :prog-bar-height="progBarHeight"
                    >
                      <v-list-tile
                        slot-scope="{tile}"
                        :key="tile.sip_id"
                        :disabled="sipCount === 0"
                        :inactive="sipCount === 0"
                        @click="showExtended = true; displayItemDetails(tile.sip_id, $event);"
                        :class="{ secondary: tile.sip_id === extendedId }"
                      >
                        <v-list-tile-content>
                          <v-list-tile-title>{{ tile.label }}</v-list-tile-title>
                        </v-list-tile-content>
                      </v-list-tile>
                    </fact-sheet-sip>
                </v-list-group>
              </v-list>
            </v-card>
            <v-divider></v-divider>
          </v-flex>
          <!-- ▲▲▲ ---- BASIC fact sheet ---- ▲▲▲ -->
          <v-divider vertical></v-divider>
          <!-- ▼▼▼ ---- EXTENDED fact sheet ---- ▼▼▼ -->
          <fact-sheet-extended
            v-if="showExtended"
            :item-id="extendedId"
            :width="factSheetWidth - 300"
            :height="height"
          >
            <template slot="close-extended">
              <!-- extended CLOSE BUTTON -->
              <v-list-tile-action @click="showExtended = false">
                <v-btn icon>
                  <v-icon>close</v-icon>
                </v-btn>
              </v-list-tile-action>
            </template>
          </fact-sheet-extended>
          <!-- ▲▲▲ ---- EXTENDED fact sheet ---- ▲▲▲ -->
        </v-layout>
      </v-container>
    </v-navigation-drawer>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters, mapMutations } from 'vuex'
import FactSheetGeneralinfo from './FactSheet_GeneralInfo'
import FactSheetSip from './FactSheet_Sip'
import FactSheetActors from './FactSheet_Actors'
import FactSheetExtended from './FactSheet_Extended'

export default {
  name: 'fact-sheet',
  components: {FactSheetGeneralinfo, FactSheetSip, FactSheetActors, FactSheetExtended},
  props: ['height', 'visWidth'],
  data () {
    return {
      showGeneralInfo: true,
      showSip: false,
      showPublicOrgs: false,
      showFirms: false,
      showExtended: false,
      extendedId: null,
      baseFactSheetWidth: 300,
      factSheetPaddingTop: 12, // main container in nav drawer top padding (8px) + fact sheet container top padding 4px
      headerHeight: 80, // header + subheader height: 40px each
      menuHeightCollapsed: 41, // height 4px + 1px border
      menuCarouselHeight: 77, // v-card with carousel height: 61px, top + bottom padding: 8px each
      progBarHeight: 2
    }
  },
  computed: {
    ...mapState([
      'profile',
      'topicMap',
      'sipsMinimal',
      'sipsFull',
      'indicators',
      'knowledgeTypes',
      'profileIndicators',
      'publicOrgsMinimal',
      'publicOrgsFull',
      'activePublicOrgsCategory',
      'firmsMinimal',
      'firmsFull',
      'activeFirmsCategory',
      'factSheetItemsHydrated'
    ]),
    ...mapGetters([
      'getCountryFromRegionCode',
      'profileIndicatorsMeta'
    ]),
    showFactSheet: {
      // vuex workaround for using store with v-model (get/set with the same keyword)
      // see https://vuex.vuejs.org/guide/forms.html#two-way-computed-property
      get () {
        return this.$store.state.showFactSheet
      },
      set (visVal) {
        this.$store.commit('setShowFactSheet', visVal)
      }
    },
    // workaround to attach fact sheet to second headline ("Show...")
    marginTop () {
      return window.innerHeight - this.height
    },
    generalInfo () {
      // put together content for General Info fold-out section in basic fact sheet
      let obj = {title: 'General Information'}
      let content = []
      let pIndicators = this.profileIndicators
      for (let i = 0; i < pIndicators.length; i++) {
        let pid = pIndicators[i]
        if (this.profile[pid.value_key] !== undefined) {
          let pIndicatorMeta = this.profileIndicatorsMeta.find(m => m.id === pid.indicator_id)
          // let item = {...pIndicator, ...pIndicatorMeta}
          if (pIndicatorMeta) {
            let item = {}
            item.value = this.profile[pid.value_key]
            item.label = pIndicatorMeta.ktype_name
            item.description = pIndicatorMeta.indicator_description
            if (pid.rank_key) {
              if (this.profile[pid.rank_key] !== undefined && this.profile[pid.rank_key] !== null) {
                item.label = item.label + ' (Rank ' + this.profile[pid.rank_key] + ')'
              } else { item.label = item.label + ' (Rank ' + 'N/A' + ')' }
            }
            content.push(item)
          }
        }
      }
      obj.data = content
      return obj
    },
    siProjects () {
      // content for SIP fold-out section in basic fact sheet
      return {title: 'Social Innovation Projects', data: this.sipsMinimal}
    },
    topPublicOrgs () {
      // content for top public orgs fold-out section in basic fact sheet
      return {title: 'Public Organisations', data: this.publicOrgsMinimal}
    },
    activePublicOrgsCatId () {
      return this.activePublicOrgsCategory.indicator_id
    },
    selectedPublicOrgs () {
      return {
        headline: 'Leading organisations',
        label: this.profileIndicatorsMeta.find(m => m.id === this.activePublicOrgsCategory.indicator_id).ktype_name,
        data: this.topPublicOrgs.data[this.activePublicOrgsCatId]
      }
    },
    topFirms () {
      // content for top public orgs fold-out section in basic fact sheet
      return {title: 'Private Companies', data: this.firmsMinimal}
    },
    activeFirmsCatId () {
      return this.activeFirmsCategory.indicator_id
    },
    selectedFirms () {
      return {
        headline: 'Top firms',
        label: this.profileIndicatorsMeta.find(m => m.id === this.activeFirmsCategory.indicator_id).ktype_name,
        data: this.topFirms.data[this.activeFirmsCatId]
      }
    },
    // put together values to display in BASIC fact sheet based on loaded profile
    // TODO maybe some of the info can actually be transported within the profile that's being passed around?
    profileHeader () {
      let header = {
        title: null,
        subtitle: null,
        yearInfo: 'Year: ' + this.profile.year,
        complementInfo: null
      }
      if (this.profile.country) {
        header.title = this.profile.country_name
      } else if (this.profile.region) {
        header.title = this.profile.region_name
        header.subtitle = this.getCountryFromRegionCode(this.profile.region_code)
      } else if (this.profile.class) {
        header.title = this.topicMap.get(this.profile.class).text
        if (this.topicMap.get(this.profile.class).parent !== '') {
          header.subtitle = this.topicMap.get(this.topicMap.get(this.profile.class).parent).text
        }
      }
      if (this.profile.complement) {
        header.complementInfo = this.profile.complement.name + ': ' + this.profile.complementItem.text
      }
      return header
    },
    // extended width is dependent on current visualisation size/width
    // TODO think about how to handle the filter side bar affecting width
    // e.g. force close one nav drawer when the other is being interacted with when there is little screen space
    factSheetWidth () {
      if (this.showExtended) {
        return this.visWidth
      } else {
        return this.baseFactSheetWidth
      }
    },
    sipProgressBar () {
      // TODO simplify once total SIP counts in profiles are reliable in API
      if (this.profile.complement) {
        if (this.profile.complementItem.value !== 'all' && this.sipsFull.length !== this.sipCount) {
          return true
        } else if (this.profile.complementItem.value === 'all' && this.sipsFull.length === 0) {
          return true
        }
      }
      return false
    },
    publicOrgsProgressBar () {
      return false
    },
    firmsProgressBar () {
      return false
    },
    simpleMenuSectionHeight () {
      // avail. height for simple dropdown menu at top of fact sheet when ignoring space requ. of following menus
      // from available nav drawer height subtract: nav drawer container + fact sheet container top padding,
      // fact sheet header + subheader section, 2px for visible lines when opening a menu
      return this.height - this.factSheetPaddingTop - this.headerHeight + 2
    },
    scrollListHeight () {
      // avail. height for top-placed menu containing a longer, scrollable list of items when opened
      // from simple menu height subtract height of progress bar
      return this.simpleMenuSectionHeight - this.progBarHeight
    },
    scrollListCarouselHeight () {
      // avail. height for top-placed scrollable menu with a carousel-like selector before scroll items start
      // from scroll list height subtract height of carousel
      return this.scrollListHeight - this.menuCarouselHeight
    },
    sipListHeight () {
      // height available for SIP menu
      // use scroll list as basis, subtract space taken by menus positioned higher up
      return this.scrollListHeight - this.menuHeightCollapsed * 4
    },
    publicOrgsListHeight () {
      // height available for public orgs menu
      // use carousel scroll list as basis, subtract space taken by menus positioned higher up
      return this.scrollListCarouselHeight - this.menuHeightCollapsed * 2
    },
    firmsListHeight () {
      // height available for private companies menu
      // use carousel scroll list as basis, subtract space taken by menus positioned higher up
      return this.scrollListCarouselHeight - this.menuHeightCollapsed * 3
    },
    sipCount () {
      let totalSip = this.profile.total_sip
      if (typeof totalSip === 'undefined') { // basic fact sheet data not yet loaded
        totalSip = 0
      }
      return totalSip
    },
    publicOrgsCount () {
      let countSelected = 0
      if (typeof this.publicOrgsMinimal[this.activePublicOrgsCatId] !== 'undefined') {
        countSelected = this.publicOrgsMinimal[this.activePublicOrgsCatId].length
      }
      return countSelected
    },
    firmsCount () {
      let countSelected = 0
      if (typeof this.firmsMinimal[this.activeFirmsCatId] !== 'undefined') {
        countSelected = this.firmsMinimal[this.activeFirmsCatId].length
      }
      return countSelected
    },
    actorsCatIds () {
      return this.profileIndicators.map(a => a.indicator_id)
    }
  },
  methods: {
    ...mapActions([
      'loadSipsMinimal',
      'loadPublicOrgs',
      'loadFirms'
    ]),
    ...mapMutations([
      'setSipsMinimal',
      'setSipsFull',
      'initPublicOrgsMinimal',
      'resetPublicOrgsFull',
      'setActivePublicOrgsCategory',
      'initFirmsMinimal',
      'resetFirmsFull',
      'setActiveFirmsCategory',
      'resetFactSheetItemsHydrated'
    ]),
    displayItemDetails (id, event) {
      event.currentTarget.classList.toggle = 'active-selection'
      // clicked item data is used to fill extended fact sheet
      // TODO think about handling of SIP vs. actors
      this.extendedId = id
    },
    refreshSips () {
      // create new API query for SIPs based on new (or initial) fact sheet data
      // except when total SIP count included in basic fact sheet data is zero
      if (this.sipCount === 0) {
        // TODO inform user there are no SIPs for the selection
        // sipCount currently not reliable because sip_detail_1 contains duplicate SIP IDs on "top level"
        // (first dimension without filtering by complement)
      } else {
        if (this.sipCount !== 0 && this.sipsMinimal.length === 0) {
          // initial sip load
          this.loadSipsMinimal(this.profile)
        }
      }
    },
    refreshPublicOrgs () {
      // load new public orgs data
      // first for currently selected category, then for other categories (= indicators)
      this.loadPublicOrgs({ id: this.activePublicOrgsCatId })

      let ids = [...this.actorsCatIds]
      let index = ids.indexOf(this.activePublicOrgsCatId)
      if (index > -1) {
        ids.splice(index, 1)
      }

      ids.forEach((x) => {
        this.loadPublicOrgs({ id: x })
      })
    },
    refreshFirms () {
      // load new public orgs data
      // first for currently selected category, then for other categories (= indicators)
      this.loadFirms({ id: this.activeFirmsCatId })

      let ids = [...this.actorsCatIds]
      let index = ids.indexOf(this.activeFirmsCatId)
      if (index > -1) {
        ids.splice(index, 1)
      }

      ids.forEach((x) => {
        this.loadFirms({ id: x })
      })
    },
    switchPublicOrgsIndicator (val) {
      // triggered by slider/carousel for selecting top public orgs by indicator
      // implemented as "endless loop" - user can click on one of the two selector arrows only
      // and still loop through all available indicators
      // val is -1 or 1
      let curIndex = this.actorsCatIds.findIndex(i => i === this.activePublicOrgsCatId)
      let countPublicOrgCategories = this.actorsCatIds.length

      let newIndex = curIndex + val
      if (newIndex === countPublicOrgCategories) {
        newIndex = 0
      } else if (newIndex < 0) {
        newIndex = countPublicOrgCategories - 1
      }
      this.setActivePublicOrgsCategory(this.profileIndicators.find(a => a.indicator_id === this.actorsCatIds[newIndex]))
    },
    switchFirmsIndicator (val) {
      // triggered by slider/carousel for selecting top public orgs by indicator
      // implemented as "endless loop" - user can click on one of the two selector arrows only
      // and still loop through all available indicators
      // val is -1 or 1
      let curIndex = this.actorsCatIds.findIndex(i => i === this.activeFirmsCatId)
      let countPublicOrgCategories = this.actorsCatIds.length

      let newIndex = curIndex + val
      if (newIndex === countPublicOrgCategories) {
        newIndex = 0
      } else if (newIndex < 0) {
        newIndex = countPublicOrgCategories - 1
      }
      this.setActiveFirmsCategory(this.profileIndicators.find(a => a.indicator_id === this.actorsCatIds[newIndex]))
    },
    resetCounts () {
      // triggered whenever basic fact sheet data changes / on new profile load

      // reset array of objects meant to get displayed in extended fact sheet
      this.resetFactSheetItemsHydrated([])

      // reset counts for SIPs or top actors IF section in question is not already empty anyway
      if (this.sipsMinimal.length > 0) {
        this.setSipsMinimal([])
        this.setSipsFull([])
      }
      if (this.publicOrgsFull.length > 0) {
        this.initPublicOrgsMinimal()
        this.resetPublicOrgsFull([])
      }
      if (this.firmsFull.length > 0) {
        this.initFirmsMinimal()
        this.resetFirmsFull([])
      }
      // load new data immediately only if a section has remained open during new profile load
      // i.e. do not interfere with lazy loading if it can be avoided
      if (this.showSip === true) {
        this.refreshSips()
      } else if (this.showPublicOrgs === true) {
        this.refreshPublicOrgs()
      } else if (this.showFirms === true) {
        this.refreshFirms()
      }
    }
  },
  watch: {
    showSip (newVal, oldVal) {
      // watches for open/close state of SIP list (or "dropdown")
      if (newVal === true) {
        this.refreshSips()
      }
    },
    showPublicOrgs (newVal, oldVal) {
      // watches for open/close state of Public Orgs list
      if (newVal === true) {
        this.refreshPublicOrgs()
      }
    },
    showFirms (newVal, oldVal) {
      // watches for open/close state of Public Orgs list
      if (newVal === true) {
        this.refreshFirms()
      }
    },
    profile (newVal, oldVal) {
      // only handle updating of SIPs + top actors when profile / fact sheet changes
      if (newVal !== oldVal) {
        this.resetCounts()
      }
    },
    showExtended (newVal, oldVal) {
      if (newVal === false) {
        // empty all display values for extended fact sheet when it is closed
        // -> avoid old data polluting basic fact sheet (some values seem to "bleed into" basic sheet from right?)
        // TODO also reset these values when the basic fact sheet is closed (in the store?)
        this.extendedId = null
      }
    }
  }
}
</script>
<style scoped lang="css">
  /* unused, for debugging */
  #fact-sheet-drawer {
  }
  #basic-fact-sheet-container {
    overflow: hidden;
  }

  .fact-sheet-list .v-list__group__header .v-list__tile__title {
    font-size: 14px;
  }
  /deep/ .fact-sheet-header .v-list__tile__content {
    justify-content: flex-end;
  }

  /deep/ .fact-sheet-header .v-list__tile__title,
  /deep/ .fact-sheet-header .v-list__tile__sub-title {
    font-size: 15px;
    font-weight: bold;
  }
  .fact-sheet-header .v-list__tile__title {
    text-transform: uppercase;
  }

  >>> .clickable-group .list-container {
    overflow-y: auto;
  }
  >>> .clickable-group .list-container >  div:last-child {
    padding-bottom: 4px;
  }

  .actor-rank {
    min-width: 30px;
  }

  /deep/ .no-grow {
    flex: 0 1 auto;
  }
</style>



// WEBPACK FOOTER //
// src/components/FactSheet.vue